/**
 * Job Scheduler Report — shows all 7 Celery beat jobs from both backend
 * services with status, schedule, and manual trigger capability.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Clock,
  Loader2,
  Play,
  RefreshCw,
  Server,
  Timer,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { jobSchedulerApi, type JobRow } from './api';

const SERVICE_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  'Product Backend': { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-400' },
  'Notification Backend': { bg: 'bg-sky-50', text: 'text-sky-700', dot: 'bg-sky-400' },
};

export function JobSchedulerPage() {
  const queryClient = useQueryClient();

  const jobsQuery = useQuery({
    queryKey: ['job-scheduler'],
    queryFn: () => jobSchedulerApi.list(),
    refetchInterval: 30_000,
  });

  const triggerMutation = useMutation({
    mutationFn: (taskName: string) => jobSchedulerApi.trigger(taskName),
    onSuccess: (_, taskName) => {
      toast.success(`Job "${taskName}" triggered successfully`);
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['job-scheduler'] }), 2000);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to trigger job');
    },
  });

  const jobs = jobsQuery.data ?? [];
  const productJobs = jobs.filter((j) => j.service === 'Product Backend');
  const notifyJobs = jobs.filter((j) => j.service === 'Notification Backend');

  if (jobsQuery.isLoading && !jobsQuery.isPlaceholderData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-[#008ED3]" />
        <span className="ml-2 text-[13px] text-slate-500">Loading job scheduler…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Timer size={20} className="text-[#008ED3]" />
            Job Scheduler Report
          </h2>
          <p className="text-[13px] text-slate-500 mt-1">
            All {jobs.length} scheduled Celery beat jobs across both backend services
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost_blue"
            size="sm"
            onClick={() => jobsQuery.refetch()}
            disabled={jobsQuery.isFetching}
            className="gap-1.5"
          >
            <RefreshCw size={13} className={jobsQuery.isFetching ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Server size={14} className="text-slate-400" />
            <span className="text-[12px] font-medium text-slate-500 uppercase tracking-wider">Total Jobs</span>
          </div>
          <span className="text-2xl font-bold text-gray-900">{jobs.length}</span>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-violet-400" />
            <span className="text-[12px] font-medium text-slate-500 uppercase tracking-wider">Product Backend</span>
          </div>
          <span className="text-2xl font-bold text-gray-900">{productJobs.length}</span>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-sky-400" />
            <span className="text-[12px] font-medium text-slate-500 uppercase tracking-wider">Notification Backend</span>
          </div>
          <span className="text-2xl font-bold text-gray-900">{notifyJobs.length}</span>
        </div>
      </div>

      {/* Jobs table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50">
              <TableHead className="px-5 py-3 font-semibold">Job Name</TableHead>
              <TableHead className="px-5 py-3 font-semibold">Service</TableHead>
              <TableHead className="px-5 py-3 font-semibold">Schedule</TableHead>
              <TableHead className="px-5 py-3 font-semibold">Task Name</TableHead>
              <TableHead className="px-5 py-3 font-semibold text-center">Status</TableHead>
              <TableHead className="px-5 py-3 font-semibold text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((job) => {
              const svc = SERVICE_COLORS[job.service] ?? SERVICE_COLORS['Product Backend'];
              const isTriggering = triggerMutation.variables === job.task && triggerMutation.isPending;
              const isNotifyTask = job.service === 'Notification Backend';

              return (
                <TableRow key={job.name} className="group">
                  {/* Job name + description */}
                  <TableCell className="px-5 py-4">
                    <div className="flex flex-col">
                      <span className="text-[13px] font-semibold text-gray-900">
                        {job.name}
                      </span>
                      <span className="text-[11px] text-slate-400 mt-0.5 leading-relaxed max-w-xs whitespace-normal">
                        {job.description}
                      </span>
                    </div>
                  </TableCell>

                  {/* Service badge */}
                  <TableCell className="px-5 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium ${svc.bg} ${svc.text}`}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full ${svc.dot}`} />
                      {job.service}
                    </span>
                  </TableCell>

                  {/* Schedule */}
                  <TableCell className="px-5 py-4">
                    <div className="flex items-center gap-1.5">
                      <Clock size={13} className="text-slate-400" />
                      <span className="text-[13px] text-slate-600">{job.schedule}</span>
                    </div>
                    {job.scheduleType === 'cron' && (
                      <span className="text-[10px] text-slate-400 font-mono mt-0.5 block">
                        {job.scheduleValue}
                      </span>
                    )}
                  </TableCell>

                  {/* Task name */}
                  <TableCell className="px-5 py-4">
                    <code className="text-[12px] text-slate-600 bg-slate-50 px-2 py-0.5 rounded border border-slate-100 font-mono">
                      {job.task}
                    </code>
                  </TableCell>

                  {/* Status */}
                  <TableCell className="px-5 py-4 text-center">
                    <StatusBadge status={job.status === 'running' ? 'Running' : 'Idle'} />
                  </TableCell>

                  {/* Trigger action */}
                  <TableCell className="px-5 py-4 text-center">
                    {isNotifyTask ? (
                      <Button
                        variant="ghost_blue"
                        size="sm"
                        onClick={() => triggerMutation.mutate(job.task)}
                        disabled={isTriggering}
                        className="h-8 gap-1"
                      >
                        {isTriggering ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Play size={12} />
                        )}
                        {isTriggering ? 'Running…' : 'Run Now'}
                      </Button>
                    ) : (
                      <span className="text-[11px] text-slate-300 italic">
                        Product worker
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {jobs.length === 0 && !jobsQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="px-5 py-12 text-center text-gray-400">
                  No scheduled jobs found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Auto-refresh indicator */}
        <div className="px-5 py-3 bg-gray-50/50 border-t border-gray-200 flex items-center justify-between">
          <span className="text-[11px] text-slate-400">
            Auto-refreshes every 30 seconds
          </span>
          <span className="text-[11px] text-slate-400 flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </span>
        </div>
      </div>
    </div>
  );
}
